import React from 'react';
import { Link } from 'react-router-dom';
import { ProductCard, Button } from '../primitives';
import TrustpilotWidget from '../TrustpilotWidget';
import { Icon, ICON_DEFAULTS } from '../../utils/icons';
import { getImageUrl, isVideoPath } from '../../utils/imageUrl';
import api from '../../services/api';
import HeroShowcase from './HeroShowcase';
import { ageGroupLabel, ageGroupParam } from '../../utils/catalog';
import '../../pages/ContentPage.css';

const rootProps = (section, className) => {
  const layout = section.layout || {};
  const classes = [className, layout.colorScheme ? `theme-section--${layout.colorScheme}` : '', layout.width ? `theme-section--width-${layout.width}` : '', layout.hideOnMobile ? 'theme-section--hide-mobile' : '', layout.hideOnDesktop ? 'theme-section--hide-desktop' : '']
    .filter(Boolean)
    .join(' ');
  return {
    className: classes,
    'data-section-key': section.key,
    style: {
      ...(Number.isFinite(layout.spacingTop) ? { paddingTop: `${layout.spacingTop}px` } : {}),
      ...(Number.isFinite(layout.spacingBottom) ? { paddingBottom: `${layout.spacingBottom}px` } : {}),
    },
  };
};

const enabledBlocks = (section) => (section.blocks || []).filter((block) => block.enabled !== false);

const HeroSection = ({ section, context }) => {
  const { t, i18n, storeName, heroItems, currencyLabel, ageGroups, featureAgeRecommendations } = context;
  const {
    kicker, title, subtitle, image, ctaLabel, ctaUrl = '/products', secondaryCtaLabel, secondaryCtaUrl,
  } = section.settings || {};
  const showcase = !image && heroItems.length > 0;
  // The second button defaults to "Shop by age" when the store has age groups.
  const secondary = secondaryCtaLabel && secondaryCtaUrl
    ? { label: secondaryCtaLabel, url: secondaryCtaUrl }
    : (featureAgeRecommendations && ageGroups.length > 0 ? { label: t('home.hero.shopByAge', 'Shop by age'), url: '#shop-by-age' } : null);
  return (
    <section {...rootProps(section, `home-hero${(showcase || image) ? ' home-hero--media' : ''}`)}>
      <div className="home-hero-inner">
        <div className="home-hero-text">
          <p className="home-hero-eyebrow">{kicker || storeName}</p>
          <h1 className="home-hero-title">{title || t('home.hero.title')}</h1>
          <p className="home-hero-subtitle">{subtitle || t('home.hero.subtitle')}</p>
          <div className="home-hero-ctas">
            <Button as="link" to={ctaUrl || '/products'} variant="brand" size="lg" className="home-hero-cta">
              {ctaLabel || t('home.hero.exploreButton')}
            </Button>
            {secondary && (secondary.url.startsWith('#')
              ? <a href={secondary.url} className="home-hero-cta-secondary">{secondary.label}</a>
              : <Link to={secondary.url} className="home-hero-cta-secondary">{secondary.label}</Link>)}
          </div>
        </div>
        {image ? (
          <div className="hero-showcase hero-showcase--custom" role="presentation">
            <div className="hero-showcase__stage"><div className="hero-showcase__slide is-active"><img src={image} alt={title || storeName} loading="eager" /></div></div>
          </div>
        ) : showcase && (
          <HeroShowcase items={heroItems} t={t} lang={i18n.language} currencyLabel={currencyLabel} showAge={featureAgeRecommendations} />
        )}
      </div>
    </section>
  );
};

const TrustStripSection = ({ section, context }) => {
  if (context.trustItems.length === 0) return null;
  return (
    <section {...rootProps(section, 'home-trust')}>
      <div className="container">
        <ul className="home-trust-row">
          {context.trustItems.map(({ id, icon: TrustIcon }) => {
            const vars = context.trustVars(id);
            return (
              <li key={id} className="home-trust-item">
                <span className="home-trust-icon-wrap"><TrustIcon size={20} strokeWidth={ICON_DEFAULTS.strokeWidth} aria-hidden="true" className="home-trust-icon" /></span>
                <div className="home-trust-text">
                  <span className="home-trust-title">{context.t(`home.trust.${id}.title`, vars)}</span>
                  <span className="home-trust-subtitle">{context.t(`home.trust.${id}.subtitle`, vars)}</span>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
};

const AGE_TILE_TONES = ['sky', 'mint', 'sun', 'coral', 'lilac'];
// Category tiles take the theme's pastels in turn, not per-category colours.
const COLLECTION_TONES = ['sky', 'sun', 'mint', 'lilac', 'coral'];

const AgeTilesSection = ({ section, context }) => {
  const { t, ageGroups, ageTileImages, featureAgeRecommendations } = context;
  if (!featureAgeRecommendations || ageGroups.length === 0) return null;
  return (
    <section {...rootProps(section, 'home-ages')} id="shop-by-age">
      <div className="container">
        <div className="home-section-head">
          <h2>{section.settings?.heading || t('home.ages.title', 'Shop by age')}</h2>
        </div>
        <ul className={`home-ages-grid home-ages-grid--${Math.min(ageGroups.length, 4)}`}>
          {ageGroups.map((group, index) => {
            const image = ageTileImages[ageGroupParam(group)];
            return (
              <li key={ageGroupParam(group)}>
                <Link to={`/products?age=${ageGroupParam(group)}`} className={`home-age-tile home-age-tile--${AGE_TILE_TONES[index % AGE_TILE_TONES.length]}`}>
                  <span className="home-age-tile__text">
                    <span className="home-age-tile__range">{ageGroupLabel(t, group)}</span>
                    <span className="home-age-tile__count">{t('home.ages.count', { count: group.count, defaultValue: `${group.count} products` })}</span>
                  </span>
                  {image && <img src={getImageUrl(image, { size: 'thumb' })} alt="" loading="lazy" onError={(event) => { if (!event.target.dataset.fallback) { event.target.dataset.fallback = '1'; event.target.src = getImageUrl(image); } }} />}
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
};

const ReviewsSection = ({ section }) => (
  <section {...rootProps(section, 'home-trustpilot')}><div className="container"><TrustpilotWidget /></div></section>
);

const TestimonialsSection = ({ section }) => {
  const blocks = enabledBlocks(section);
  if (blocks.length === 0) return null;
  return (
    <section {...rootProps(section, 'home-testimonials')}>
      <div className="container">
        {section.settings?.heading && <h2>{section.settings.heading}</h2>}
        <ul className="home-testimonials-grid">
          {blocks.map((block) => (
            <li key={block.key} className="home-testimonial-card">
              <p className="home-testimonial-quote">&ldquo;{block.settings?.quote}&rdquo;</p>
              {block.settings?.author && <p className="home-testimonial-author">{block.settings.author}</p>}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

const ProductCarousel = ({ products, label = 'Products' }) => {
  const scrollerRef = React.useRef(null);
  const move = (direction) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    scroller.scrollBy({ left: direction * Math.max(260, scroller.clientWidth * 0.8), behavior: 'smooth' });
  };
  return (
    <div className="featured-carousel-shell">
      <button type="button" className="featured-carousel__control previous" onClick={() => move(-1)} aria-label="Previous products"><Icon.ChevronLeft size={18} /></button>
      <div ref={scrollerRef} className="featured-carousel" role="list" aria-label={label}>
        {products.map((product) => <div className="featured-carousel__item" role="listitem" key={product.id}><ProductCard product={product} /></div>)}
      </div>
      <button type="button" className="featured-carousel__control next" onClick={() => move(1)} aria-label="Next products"><Icon.ChevronRight size={18} /></button>
    </div>
  );
};

const ProductDisplay = ({ products, display, label }) => display === 'carousel'
  ? <ProductCarousel products={products} label={label} />
  : <div className="featured-grid">{products.map((product) => <ProductCard key={product.id} product={product} />)}</div>;

const FeaturedProductsSection = ({ section, context }) => {
  const limit = section.settings?.limit || 8;
  const products = context.featuredProducts.slice(0, limit);
  const display = section.settings?.display || 'grid';
  const subtitle = section.settings?.subtitle;
  return (
    <section {...rootProps(section, 'featured-section')} id="featured">
      <div className="container">
        <div className="home-section-head">
          <div>
            <h2>{section.settings?.heading || context.t('home.featured.title')}</h2>
            {subtitle && <p>{subtitle}</p>}
          </div>
          <Link to="/products" className="home-section-link">{context.t('home.seeAll', 'See all')} <Icon.ChevronRight size={16} aria-hidden="true" /></Link>
        </div>
        {products.length > 0 ? (
          <ProductDisplay products={products} display={display} label={section.settings?.heading || context.t('home.featured.title')} />
        ) : (
          <div className="empty-state">
            <h3>{context.t('home.empty.title', 'Products coming soon')}</h3>
            <p>{context.t('home.empty.subtitle', 'We are preparing something great for you.')}</p>
          </div>
        )}
      </div>
    </section>
  );
};

const FeaturedCollectionSection = ({ section, context }) => {
  const categoryId = section.settings?.categoryId;
  const limit = section.settings?.limit || 6;
  const display = section.settings?.display || 'grid';
  const category = (context.categories || []).find((item) => Number(item.id) === Number(categoryId));
  const [products, setProducts] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!category?.slug) {
      setProducts([]);
      return undefined;
    }
    let active = true;
    setLoading(true);
    api.get(`/api/products?categorySlug=${encodeURIComponent(category.slug)}&limit=${limit}`)
      .then((response) => { if (active) setProducts(response.data?.products || []); })
      .catch(() => { if (active) setProducts([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [category?.slug, limit]);

  if (!category) {
    return context.isEditorPreview ? <section {...rootProps(section, 'featured-section')}><div className="container"><div className="empty-state"><h3>Choose a collection</h3><p>Select a collection in the editor to feature its products here.</p></div></div></section> : null;
  }

  return (
    <section {...rootProps(section, 'featured-section')}>
      <div className="container">
        <div className="featured-header"><h2>{section.settings?.heading || category.name}</h2><p>{category.description || ''}</p></div>
        {loading ? <div className="empty-state"><p>Loading collection...</p></div> : products.length > 0 ? <ProductDisplay products={products} display={display} label={`${category.name} products`} /> : <div className="empty-state"><h3>Products coming soon</h3><p>This collection does not have available products yet.</p></div>}
      </div>
    </section>
  );
};

const RecommendedProductsSection = ({ section, context }) => {
  const { heading, subtitle, source = 'popular', limit = 6, display = 'grid' } = section.settings || {};
  const [products, setProducts] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const sourceLabel = source === 'newest' ? 'New arrivals' : 'Popular picks';
  const url = source === 'newest'
    ? `/api/products?sortBy=newest&limit=${limit}`
    : `/api/products?popular=true&limit=${limit}`;

  React.useEffect(() => {
    let active = true;
    setLoading(true);
    api.get(url)
      .then((response) => { if (active) setProducts(response.data?.products || []); })
      .catch(() => { if (active) setProducts([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [url]);

  if (!loading && products.length === 0 && !context.isEditorPreview) return null;

  return (
    <section {...rootProps(section, 'featured-section')}>
      <div className="container">
        <div className="featured-header"><h2>{heading || sourceLabel}</h2>{subtitle && <p>{subtitle}</p>}</div>
        {loading ? <div className="empty-state"><p>Loading recommendations...</p></div> : products.length > 0 ? <ProductDisplay products={products} display={display} label="Recommended products" /> : context.isEditorPreview ? <div className="empty-state"><h3>No products available</h3><p>Add curated popular products or publish products to populate this section.</p></div> : null}
      </div>
    </section>
  );
};

const FeaturedProductSection = ({ section, context }) => {
  const productId = section.settings?.productId;
  const [product, setProduct] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!productId) {
      setProduct(null);
      return undefined;
    }
    let active = true;
    setLoading(true);
    api.get(`/api/products/by-id/${productId}`)
      .then((response) => { if (active) setProduct(response.data || null); })
      .catch(() => { if (active) setProduct(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [productId]);

  if (!productId) {
    return context.isEditorPreview ? <section {...rootProps(section, 'featured-section featured-product-section')}><div className="container"><div className="empty-state"><h3>Choose a product</h3><p>Select a product in the editor to feature it here.</p></div></div></section> : null;
  }

  if (!loading && !product) {
    return context.isEditorPreview ? <section {...rootProps(section, 'featured-section featured-product-section')}><div className="container"><div className="empty-state"><h3>Product unavailable</h3><p>Choose another product or restore the selected product.</p></div></div></section> : null;
  }

  return (
    <section {...rootProps(section, 'featured-section featured-product-section')}>
      <div className="container">
        {section.settings?.heading && <div className="featured-header"><h2>{section.settings.heading}</h2></div>}
        {loading ? <div className="empty-state"><p>Loading product...</p></div> : <div className="featured-product-section__card"><ProductCard product={product} showActions={false} /></div>}
      </div>
    </section>
  );
};

const PageLinksSection = ({ section, context }) => {
  const blocks = enabledBlocks(section).filter((block) => block.settings?.pageUrl);
  if (blocks.length === 0) {
    return context.isEditorPreview ? <section {...rootProps(section, 'content-page-links')}><div className="container"><div className="empty-state"><h3>Add page links</h3><p>Choose published pages to help visitors explore your store.</p></div></div></section> : null;
  }
  return (
    <section {...rootProps(section, 'content-page-links')}>
      <div className="container">
        {section.settings?.heading && <h2>{section.settings.heading}</h2>}
        <ul className="content-page-links__list">
          {blocks.map((block) => <li key={block.key}><Link to={block.settings.pageUrl}>{block.settings.label || block.settings.pageUrl.replace(/^\//, '').split('?')[0]}</Link></li>)}
        </ul>
      </div>
    </section>
  );
};

const countdownParts = (endsAt, now) => {
  const target = Date.parse(endsAt || '');
  if (!Number.isFinite(target)) return null;
  const remaining = Math.max(0, target - now);
  return {
    expired: remaining === 0,
    days: Math.floor(remaining / 86400000),
    hours: Math.floor((remaining % 86400000) / 3600000),
    minutes: Math.floor((remaining % 3600000) / 60000),
    seconds: Math.floor((remaining % 60000) / 1000),
  };
};

const CountdownSection = ({ section, context }) => {
  const { heading, body, endsAt, expiredText, ctaLabel, ctaUrl } = section.settings || {};
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (!endsAt) return undefined;
    setNow(Date.now());
    const interval = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [endsAt]);
  const parts = countdownParts(endsAt, now);

  if (!parts) {
    return context.isEditorPreview ? <section {...rootProps(section, 'content-countdown')}><div className="container"><div className="empty-state"><h2>Set an end date</h2><p>Choose when this campaign should end to preview the countdown.</p></div></div></section> : null;
  }

  const units = [['Days', parts.days], ['Hours', parts.hours], ['Minutes', parts.minutes], ['Seconds', parts.seconds]];
  return (
    <section {...rootProps(section, 'content-countdown')}>
      <div className="container content-countdown__inner">
        {heading && <h2>{heading}</h2>}
        {body && <p className="content-countdown__body">{body}</p>}
        {parts.expired ? <p className="content-countdown__expired">{expiredText || 'This offer has ended.'}</p> : <dl className="content-countdown__units">{units.map(([label, value]) => <div key={label}><dd>{String(value).padStart(2, '0')}</dd><dt>{label}</dt></div>)}</dl>}
        {!parts.expired && ctaLabel && ctaUrl && (/^https?:\/\//i.test(ctaUrl) ? <a className="content-countdown__cta" href={ctaUrl}>{ctaLabel}</a> : <Link className="content-countdown__cta" to={ctaUrl}>{ctaLabel}</Link>)}
      </div>
    </section>
  );
};

const BeforeAfterSection = ({ section, context }) => {
  const { heading, body, beforeImage, afterImage, beforeLabel, afterLabel, initialPosition = 50 } = section.settings || {};
  const initial = Math.max(10, Math.min(90, Number(initialPosition) || 50));
  const [position, setPosition] = React.useState(initial);
  React.useEffect(() => setPosition(initial), [initial]);

  if (!beforeImage || !afterImage) {
    return context.isEditorPreview ? <section {...rootProps(section, 'content-before-after')}><div className="container"><div className="empty-state"><h2>Choose before and after images</h2><p>Select two images to build an interactive comparison.</p></div></div></section> : null;
  }

  return (
    <section {...rootProps(section, 'content-before-after')}>
      <div className="container content-before-after__inner">
        {(heading || body) && <header>{heading && <h2>{heading}</h2>}{body && <p>{body}</p>}</header>}
        <div className="content-before-after__comparison">
          <img className="content-before-after__image after" src={afterImage} alt={afterLabel || 'After'} loading="lazy" />
          <img className="content-before-after__image before" src={beforeImage} alt={beforeLabel || 'Before'} loading="lazy" style={{ clipPath: `inset(0 ${100 - position}% 0 0)` }} />
          <span className="content-before-after__divider" aria-hidden="true" style={{ left: `${position}%` }} />
          <span className="content-before-after__handle" aria-hidden="true" style={{ left: `${position}%` }}><Icon.ChevronLeft size={14} /><Icon.ChevronRight size={14} /></span>
          <input className="content-before-after__slider" type="range" min="10" max="90" value={position} onChange={(event) => setPosition(Number(event.target.value))} aria-label="Comparison position" />
          <span className="content-before-after__label before">{beforeLabel || 'Before'}</span>
          <span className="content-before-after__label after">{afterLabel || 'After'}</span>
        </div>
      </div>
    </section>
  );
};

const LookbookSection = ({ section, context }) => {
  const { heading, body, image } = section.settings || {};
  const hotspots = enabledBlocks(section).filter((block) => block.settings?.productId);
  const productIds = React.useMemo(
    () => [...new Set(enabledBlocks(section).map((block) => Number(block.settings?.productId)).filter(Number.isInteger))],
    [section]
  );
  const [products, setProducts] = React.useState({});

  React.useEffect(() => {
    if (productIds.length === 0) {
      setProducts({});
      return undefined;
    }
    let active = true;
    Promise.all(productIds.map((productId) => api.get(`/api/products/by-id/${productId}`)
      .then((response) => [productId, response.data || null])
      .catch(() => [productId, null])))
      .then((entries) => {
        if (!active) return;
        setProducts(Object.fromEntries(entries.filter(([, product]) => product?.slug)));
      });
    return () => { active = false; };
  }, [productIds]);

  if (!image) {
    return context.isEditorPreview ? <section {...rootProps(section, 'content-lookbook')}><div className="container"><div className="empty-state"><h2>Choose a lookbook image</h2><p>Add a campaign image, then place product hotspots over it.</p></div></div></section> : null;
  }

  return (
    <section {...rootProps(section, 'content-lookbook')}>
      <div className="container content-lookbook__inner">
        {(heading || body) && <header>{heading && <h2>{heading}</h2>}{body && <p>{body}</p>}</header>}
        <div className="content-lookbook__media">
          <img src={image} alt={heading || 'Shop the look'} loading="lazy" />
          {hotspots.map((hotspot) => {
            const product = products[hotspot.settings.productId];
            if (!product) return null;
            const label = hotspot.settings.label || product.name;
            return <Link key={hotspot.key} className={`content-lookbook__hotspot content-lookbook__hotspot--${hotspot.settings.position || 'center'}`} to={`/products/${product.slug}`} aria-label={label}><span aria-hidden="true">+</span><strong>{label}</strong></Link>;
          })}
        </div>
      </div>
    </section>
  );
};

const InventoryUrgencySection = ({ section, context }) => {
  const { heading, body, productId, ctaLabel } = section.settings || {};
  const [product, setProduct] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!productId) {
      setProduct(null);
      return undefined;
    }
    let active = true;
    setLoading(true);
    api.get(`/api/products/by-id/${productId}`)
      .then((response) => { if (active) setProduct(response.data || null); })
      .catch(() => { if (active) setProduct(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [productId]);

  if (!productId) {
    return context.isEditorPreview ? <section {...rootProps(section, 'content-inventory-urgency')}><div className="container"><div className="empty-state"><h2>Choose a product</h2><p>This alert appears only when the selected product has low live stock.</p></div></div></section> : null;
  }

  if (loading) return context.isEditorPreview ? <section {...rootProps(section, 'content-inventory-urgency')}><div className="container"><div className="empty-state"><p>Checking live inventory...</p></div></div></section> : null;

  if (!product || product.stockStatus !== 'low_stock') {
    return context.isEditorPreview ? <section {...rootProps(section, 'content-inventory-urgency')}><div className="container"><div className="empty-state"><h2>Alert is currently hidden</h2><p>It will appear automatically when {product?.name || 'the selected product'} reaches your configured low-stock threshold.</p></div></div></section> : null;
  }

  const label = ctaLabel || `Shop ${product.name}`;
  return (
    <section {...rootProps(section, 'content-inventory-urgency')}>
      <div className="container content-inventory-urgency__inner">
        <div><p className="content-inventory-urgency__eyebrow">Low stock</p><h2>{heading || `${product.name} is running low`}</h2>{body && <p>{body}</p>}</div>
        <Link to={`/products/${product.slug}`} className="content-inventory-urgency__cta">{label}</Link>
      </div>
    </section>
  );
};

const PromotionBannerSection = ({ section, context }) => {
  const { eyebrow, heading, body, image, style = 'compact', ctaLabel, ctaUrl } = section.settings || {};
  if (!eyebrow && !heading && !body && !image) {
    return context.isEditorPreview ? <section {...rootProps(section, 'content-promotion')}><div className="container"><div className="empty-state"><h2>Create a promotion</h2><p>Add a heading, supporting copy, or an image to preview this campaign banner.</p></div></div></section> : null;
  }
  const cta = ctaLabel && ctaUrl && (/^https?:\/\//i.test(ctaUrl)
    ? <a className="content-promotion__cta" href={ctaUrl}>{ctaLabel}</a>
    : <Link className="content-promotion__cta" to={ctaUrl}>{ctaLabel}</Link>);
  return (
    <section {...rootProps(section, `content-promotion content-promotion--${style}`)}>
      <div className="container">
        <div className="content-promotion__banner">
          {image && <img className="content-promotion__image" src={image} alt="" loading="lazy" />}
          <div className="content-promotion__content">
            {eyebrow && <p className="content-promotion__eyebrow">{eyebrow}</p>}
            {heading && <h2>{heading}</h2>}
            {body && <p className="content-promotion__body">{body}</p>}
            {cta}
          </div>
        </div>
      </div>
    </section>
  );
};

const CollectionsSection = ({ section, context }) => {
  const categories = context.categories.filter((category) => category.productCount !== 0).slice(0, section.settings?.limit || 3);
  if (categories.length === 0) return null;
  const heading = section.settings?.heading;
  return (
    <section {...rootProps(section, 'home-collections')}>
      <div className="container">
        {heading && <div className="home-section-head"><h2>{heading}</h2></div>}
        <div className={`home-collections-grid home-collections-grid--${Math.min(categories.length, 3)}`}>
          {categories.map((category, index) => {
            const images = category.image ? [category.image] : (context.categoryImages[category.slug] || []);
            return (
              <Link
                key={category.id}
                to={`/products?category=${category.slug}`}
                className={`home-collection-card home-collection-card--${COLLECTION_TONES[index % COLLECTION_TONES.length]}`}
              >
                <span className="home-collection-label">
                  <strong>{category.name}</strong>
                  {category.productCount > 0 && <span>{context.t('home.ages.count', { count: category.productCount, defaultValue: `${category.productCount} products` })}</span>}
                  <span className="home-collection-cta">{context.t('home.shopBy.browse', 'Browse')} <Icon.ChevronRight size={15} aria-hidden="true" /></span>
                </span>
                {images.length > 0 && (
                  <span className={`home-collection-plates home-collection-plates--${images.length}`} aria-hidden="true">
                    {images.map((image) => (
                      <img key={image} src={getImageUrl(image, { size: 'thumb' })} alt="" loading="lazy" onError={(event) => { if (!event.target.dataset.fallback) { event.target.dataset.fallback = '1'; event.target.src = getImageUrl(image); } }} />
                    ))}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const ImageWithTextSection = ({ section }) => {
  const { heading, body, image, imagePosition = 'left', imageRatio = 'natural', contentAlignment = 'start', ctaLabel, ctaUrl } = section.settings || {};
  if (!heading && !body && !image) return null;
  return (
    <section {...rootProps(section, `home-split home-split--media-${imagePosition} home-split--ratio-${imageRatio} home-split--content-${contentAlignment}`)}>
      <div className="container home-split-inner">
        {image && <div className="home-split-media"><img src={image} alt={heading || ''} loading="lazy" /></div>}
        <div className="home-split-text">
          {heading && <h2 className="home-split-title">{heading}</h2>}
          {body && <p className="home-split-body">{body}</p>}
          {ctaLabel && ctaUrl && <Button as="link" to={ctaUrl} variant="brand" size="md" className="home-split-cta">{ctaLabel}</Button>}
        </div>
      </div>
    </section>
  );
};

const VideoSection = ({ section, context }) => {
  const { heading, body, video, poster, autoplay, loop } = section.settings || {};
  if (!video) {
    return context.isEditorPreview ? <section {...rootProps(section, 'content-video')}><div className="container"><div className="empty-state"><h2>Choose a video</h2><p>Select a video from the media library to preview this section.</p></div></div></section> : null;
  }
  return (
    <section {...rootProps(section, 'content-video')}>
      <div className="container content-video__inner">
        {(heading || body) && <header className="content-video__header">{heading && <h2>{heading}</h2>}{body && <p>{body}</p>}</header>}
        <video className="content-video__player" controls playsInline preload="metadata" poster={poster || undefined} autoPlay={!!autoplay} loop={!!loop} muted={!!autoplay}>
          <source src={video} />
          Your browser does not support embedded video.
        </video>
      </div>
    </section>
  );
};

const ContactSection = ({ section, context }) => {
  const { heading, body, showAddress = true, showPhone = true, showEmail = true, showNipt = false, showWhatsApp = true, whatsAppLabel } = section.settings || {};
  const contact = context.contact || {};
  const whatsAppNumber = String(contact.whatsAppPhone || '').replace(/\D/g, '');
  const mapQuery = contact.address || (contact.mapLat && contact.mapLng ? `${contact.mapLat},${contact.mapLng}` : '');
  const details = [
    showAddress && contact.address && { key: 'address', icon: Icon.Location, label: context.t('contact.location', 'Visit us'), value: contact.address, href: mapQuery ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapQuery)}` : null },
    showPhone && contact.phone && { key: 'phone', icon: Icon.Phone, label: context.t('contact.phone', 'Call us'), value: contact.phone, href: `tel:${contact.phone.replace(/[^+\d]/g, '')}` },
    showEmail && contact.email && { key: 'email', icon: Icon.Email, label: context.t('contact.email', 'Email us'), value: contact.email, href: `mailto:${contact.email}` },
    showNipt && contact.nipt && { key: 'nipt', icon: Icon.FiscalExport, label: context.t('contact.nipt', 'NIPT'), value: contact.nipt },
    showWhatsApp && contact.whatsAppEnabled && whatsAppNumber && { key: 'whatsapp', icon: Icon.Phone, label: whatsAppLabel || context.t('contact.whatsapp', 'Message on WhatsApp'), value: contact.whatsAppPhone, href: `https://wa.me/${whatsAppNumber}` },
  ].filter(Boolean);
  if (details.length === 0) {
    return context.isEditorPreview ? <section {...rootProps(section, 'content-contact')}><div className="container"><div className="empty-state"><h2>Configure store contact details</h2><p>Add a phone, email, or address in Settings to show this section.</p></div></div></section> : null;
  }
  return (
    <section {...rootProps(section, 'content-contact')}>
      <div className="container">
        {(heading || body) && <header className="content-contact__header">{heading && <h2>{heading}</h2>}{body && <p>{body}</p>}</header>}
        <ul className="content-contact__grid">{details.map(({ key, icon: ContactIcon, label, value, href }) => <li key={key} className="content-contact__item"><ContactIcon size={21} aria-hidden="true" /><div><strong>{label}</strong>{href ? <a href={href} {...(key === 'address' || key === 'whatsapp' ? { target: '_blank', rel: 'noreferrer' } : {})}>{value}</a> : <span>{value}</span>}</div></li>)}</ul>
      </div>
    </section>
  );
};

const ContactFormSection = ({ section, context }) => {
  const { heading, body, subject, submitLabel, successMessage } = section.settings || {};
  const [form, setForm] = React.useState({ name: '', email: '', message: '' });
  const [status, setStatus] = React.useState('idle');
  const [error, setError] = React.useState('');
  const formId = `contact-form-${section.key}`;
  const update = (key) => (event) => {
    setForm((current) => ({ ...current, [key]: event.target.value }));
    if (error) setError('');
  };
  const submit = async (event) => {
    event.preventDefault();
    const payload = { name: form.name.trim(), email: form.email.trim(), message: form.message.trim() };
    if (!payload.name || !payload.message || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payload.email)) {
      setError('Enter your name, a valid email address, and a message.');
      return;
    }
    if (context.isEditorPreview) {
      setStatus('preview');
      return;
    }
    setStatus('submitting');
    setError('');
    try {
      await api.post('/api/contact', { ...payload, subject: subject || heading || 'Store inquiry' });
      setStatus('success');
      setForm({ name: '', email: '', message: '' });
    } catch (requestError) {
      setStatus('idle');
      setError(requestError.response?.data?.error || 'Your message could not be sent. Please try again.');
    }
  };

  return (
    <section {...rootProps(section, 'content-contact-form')}>
      <div className="container content-contact-form__inner">
        {(heading || body) && <header>{heading && <h2>{heading}</h2>}{body && <p>{body}</p>}</header>}
        <form className="content-contact-form__form" onSubmit={submit} noValidate>
          <div className="content-contact-form__row">
            <label htmlFor={`${formId}-name`}>Name<input id={`${formId}-name`} type="text" value={form.name} onChange={update('name')} autoComplete="name" required /></label>
            <label htmlFor={`${formId}-email`}>Email<input id={`${formId}-email`} type="email" value={form.email} onChange={update('email')} autoComplete="email" required /></label>
          </div>
          <label htmlFor={`${formId}-message`}>Message<textarea id={`${formId}-message`} rows="5" value={form.message} onChange={update('message')} required /></label>
          {error && <p className="content-contact-form__alert error" role="alert">{error}</p>}
          {status === 'success' && <p className="content-contact-form__alert success" role="status">{successMessage || 'Thanks, your message has been sent.'}</p>}
          {status === 'preview' && <p className="content-contact-form__alert" role="status">Preview only: messages are not sent from the editor.</p>}
          <Button type="submit" variant="brand" size="md" disabled={status === 'submitting'}>{status === 'submitting' ? 'Sending...' : submitLabel || 'Send message'}</Button>
        </form>
      </div>
    </section>
  );
};

const FeatureColumnsSection = ({ section }) => {
  const blocks = enabledBlocks(section);
  if (blocks.length === 0) return null;
  return (
    <section {...rootProps(section, 'home-feature-columns')}>
      <div className="container">
        {section.settings?.heading && <h2>{section.settings.heading}</h2>}
        <div className="home-feature-columns-grid">
          {blocks.map((block) => (
            <article key={block.key} className="home-feature-column-card">
              {block.settings?.icon && <span className="home-feature-column-icon" aria-hidden="true">{block.settings.icon}</span>}
              {block.settings?.title && <h3 className="home-feature-column-title">{block.settings.title}</h3>}
              {block.settings?.description && <p className="home-feature-column-desc">{block.settings.description}</p>}
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

const StaticFeaturesSection = ({ section, context }) => {
  const cards = [
    { id: 'creative', icon: Icon.Star },
    { id: 'safe', icon: Icon.Secure },
    { id: 'educational', icon: Icon.Search },
    { id: 'gifts', icon: Icon.Wishlist },
  ];
  return (
    <section {...rootProps(section, 'home-features')}>
      <div className="container">
        <div className="home-features-header"><h2>{context.t('home.features.title')}</h2><p>{context.t('home.features.subtitle')}</p></div>
        <div className="home-features-grid">
          {cards.map(({ id, icon: FeatureIcon }) => (
            <article key={id} className="home-feature-card">
              <FeatureIcon size={26} strokeWidth={ICON_DEFAULTS.strokeWidth} aria-hidden="true" className="home-feature-icon" />
              <h3 className="home-feature-title">{context.t(`home.features.${id}.title`)}</h3>
              <p className="home-feature-desc">{context.t(`home.features.${id}.description`)}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

const BundlesSection = ({ section, context }) => {
  const bundles = context.bundles.slice(0, section.settings?.limit || 3);
  if (bundles.length === 0) return null;
  return (
    <section {...rootProps(section, 'home-bundles')} aria-labelledby={`${section.key}-heading`}>
      <div className="container">
        <header className="home-bundles__header">
          <div><h2 id={`${section.key}-heading`} className="home-bundles__title">{context.t('bundles.title')}</h2><p className="home-bundles__subtitle">{context.t('bundles.subtitle')}</p></div>
          <Button as="link" to="/bundles" variant="secondary" size="md">{context.t('bundles.viewAll')}</Button>
        </header>
        <ul className="home-bundles__list">
          {bundles.map((bundle) => {
            const items = bundle.items || [];
            const lineTotal = items.reduce((sum, item) => item.isFreeItem ? sum : sum + (parseFloat(item.productPrice) || 0) * (parseInt(item.quantity, 10) || 1), 0);
            const priceAfter = bundle.type === 'percentage'
              ? lineTotal * (1 - (parseFloat(bundle.discountValue) || 0) / 100)
              : bundle.type === 'fixed' ? Math.max(0, lineTotal - (parseFloat(bundle.discountValue) || 0)) : lineTotal;
            const previewItem = items.find((item) => item.productImage && !isVideoPath(item.productImage));
            return (
              <li key={bundle.id} className="home-bundle-tile">
                <Link to="/bundles" className="home-bundle-tile__link">
                  <div className="home-bundle-tile__media">
                    {previewItem?.productImage ? <img src={getImageUrl(previewItem.productImage, { size: 'thumb' })} alt={bundle.name} loading="lazy" /> : <div className="home-bundle-tile__placeholder" aria-hidden="true">{bundle.name?.[0] || '·'}</div>}
                  </div>
                  <div className="home-bundle-tile__body">
                    <h3 className="home-bundle-tile__name">{bundle.name}</h3>
                    <p className="home-bundle-tile__meta">{context.t('bundles.items', { count: items.reduce((sum, item) => sum + (parseInt(item.quantity, 10) || 1), 0) })}</p>
                    <p className="home-bundle-tile__price">{priceAfter.toLocaleString(context.i18n.language === 'sq' ? 'sq-AL' : 'en-US')} {context.currency}</p>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
};

const RecentlyViewedSection = ({ section, context }) => {
  const minimum = section.settings?.minimumItems || 3;
  if (context.recentlyViewed.length < minimum) return null;
  return (
    <section {...rootProps(section, 'home-recent')} aria-labelledby={`${section.key}-heading`}>
      <div className="container">
        <header className="home-recent__header">
          <h2 id={`${section.key}-heading`} className="home-recent__title">{context.t('home.recent.title', 'Pick up where you left off')}</h2>
          <p className="home-recent__subtitle">{context.t('home.recent.subtitle', 'Recently viewed by you on this device.')}</p>
        </header>
        <div className="home-recent__grid">
          {context.recentlyViewed.slice(0, section.settings?.limit || 6).map((product) => <ProductCard key={product.id} product={product} variant="compact" showActions={false} showBadges={false} showDescription={false} showCategory={false} />)}
        </div>
      </div>
    </section>
  );
};

const FaqSection = ({ section, context }) => {
  const blocks = enabledBlocks(section);
  if (blocks.length === 0) return null;
  return (
    <section {...rootProps(section, 'home-faq')} aria-labelledby={`${section.key}-heading`}>
      <div className="container">
        <h2 id={`${section.key}-heading`} className="home-faq-title">{section.settings?.heading || context.t('home.faq.title', 'Frequently asked questions')}</h2>
        <ul className="home-faq-list">
          {blocks.map((block) => {
            const itemKey = `${section.key}:${block.key}`;
            const isOpen = context.openFaqKey === itemKey;
            return (
              <li key={block.key} className="home-faq-item">
                <button type="button" className="home-faq-question" aria-expanded={isOpen} onClick={() => context.setOpenFaqKey(isOpen ? null : itemKey)}>
                  <span>{block.settings?.question}</span><span className="home-faq-icon" aria-hidden="true">{isOpen ? '−' : '+'}</span>
                </button>
                {isOpen && <p className="home-faq-answer">{block.settings?.answer}</p>}
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
};

const NewsletterSection = ({ section, context }) => (
  <section {...rootProps(section, 'home-newsletter')}>
    <div className="container">
      <div className="home-newsletter-card">
        <div className="home-newsletter-text">
          <p className="home-newsletter-eyebrow">{context.t('home.newsletter.eyebrow')}</p>
          <h2 className="home-newsletter-title">{context.t('home.newsletter.title')}</h2>
          <p className="home-newsletter-subtitle">{context.t('home.newsletter.subtitle')}</p>
        </div>
        <form className="home-newsletter-form" onSubmit={context.handleNewsletterSubmit}>
          <label htmlFor={`newsletter-email-${section.key}`} className="visually-hidden">{context.t('home.newsletter.placeholder')}</label>
          <input id={`newsletter-email-${section.key}`} type="email" className="home-newsletter-input" placeholder={context.t('home.newsletter.placeholder')} value={context.newsletterEmail} onChange={(event) => context.setNewsletterEmail(event.target.value)} required />
          <Button type="submit" variant="brand" size="md" disabled={context.newsletterSubmitting}>{context.newsletterSubmitting ? context.t('home.newsletter.submitting') : context.t('home.newsletter.cta')}</Button>
        </form>
        {context.newsletterMessage && <p className="home-newsletter-message" role="status">{context.newsletterMessage}</p>}
      </div>
    </div>
  </section>
);

const RichTextSection = ({ section }) => {
  const { heading, body, textAlign = 'left' } = section.settings || {};
  if (!heading && !body) return null;
  return (
    <section {...rootProps(section, `content-rich-text content-rich-text--${textAlign}`)}>
      <div className="container content-rich-text__inner">
        {heading && <h1 className="content-rich-text__heading">{heading}</h1>}
        {body && <div className="content-rich-text__body">{body.split(/\n{2,}/).map((paragraph, index) => <p key={`${section.key}-paragraph-${index}`}>{paragraph}</p>)}</div>}
      </div>
    </section>
  );
};

const ReusableFaqSection = ({ section, context }) => {
  const items = section.entry?.fields?.items || [];
  if (items.length === 0) return null;
  return <section {...rootProps(section, 'home-faq')}><div className="container"><h2 className="home-faq-title">{section.settings?.heading || section.entry?.title}</h2><ul className="home-faq-list">{items.map((item, index) => {
    const itemKey = `${section.key}:entry:${index}`;
    const isOpen = context.openFaqKey === itemKey;
    return <li key={itemKey} className="home-faq-item"><button type="button" className="home-faq-question" aria-expanded={isOpen} onClick={() => context.setOpenFaqKey(isOpen ? null : itemKey)}><span>{item.question}</span><span className="home-faq-icon" aria-hidden="true">{isOpen ? '−' : '+'}</span></button>{isOpen && <p className="home-faq-answer">{item.answer}</p>}</li>;
  })}</ul></div></section>;
};

const ReusableTestimonialsSection = ({ section }) => {
  const items = section.entry?.fields?.items || [];
  if (items.length === 0) return null;
  return <section {...rootProps(section, 'home-testimonials')}><div className="container">{(section.settings?.heading || section.entry?.title) && <h2>{section.settings?.heading || section.entry?.title}</h2>}<ul className="home-testimonials-grid">{items.map((item, index) => <li key={`${section.key}-${index}`} className="home-testimonial-card"><p className="home-testimonial-quote">&ldquo;{item.quote}&rdquo;</p>{item.author && <p className="home-testimonial-author">{item.author}</p>}</li>)}</ul></div></section>;
};

const BrandListSection = ({ section }) => {
  const items = section.entry?.fields?.items || [];
  if (items.length === 0) return null;
  return <section {...rootProps(section, 'content-brand-list')}><div className="container">{(section.settings?.heading || section.entry?.title) && <h2>{section.settings?.heading || section.entry?.title}</h2>}<ul className="content-brand-list__grid">{items.map((item, index) => <li key={`${section.key}-${index}`} className="content-brand-list__item">{item.url ? <a href={item.url} target="_blank" rel="noreferrer">{item.logo ? <img src={item.logo} alt={item.name} loading="lazy" /> : item.name}</a> : item.logo ? <img src={item.logo} alt={item.name} loading="lazy" /> : <span>{item.name}</span>}</li>)}</ul></div></section>;
};

const SizeGuideSection = ({ section }) => {
  const entry = section.entry;
  const columns = entry?.fields?.columns || [];
  const rows = entry?.fields?.rows || [];
  if (!entry || columns.length === 0) return null;
  return <section {...rootProps(section, 'content-size-guide')}><div className="container">{(section.settings?.heading || entry.title) && <h2>{section.settings?.heading || entry.title}</h2>}{entry.fields.body && <p>{entry.fields.body}</p>}<div className="content-size-guide__table-wrap"><table><thead><tr>{columns.map((column, index) => <th key={index}>{column}</th>)}</tr></thead><tbody>{rows.map((row, rowIndex) => <tr key={rowIndex}>{row.map((cell, cellIndex) => <td key={cellIndex}>{cell}</td>)}</tr>)}</tbody></table></div></div></section>;
};

export const HOME_SECTION_REGISTRY = {
  hero: { name: 'Hero', component: HeroSection },
  trust_strip: { name: 'Trust strip', component: TrustStripSection },
  age_tiles: { name: 'Shop by age', component: AgeTilesSection },
  reviews: { name: 'Reviews', component: ReviewsSection },
  testimonials: { name: 'Testimonials', component: TestimonialsSection },
  featured_products: { name: 'Featured products', component: FeaturedProductsSection },
  recommended_products: { name: 'Recommended products', component: RecommendedProductsSection },
  cart_recommendations: { name: 'Cart recommendations', component: RecommendedProductsSection },
  search_recommendations: { name: 'Search recommendations', component: RecommendedProductsSection },
  featured_collection: { name: 'Featured collection', component: FeaturedCollectionSection },
  featured_product: { name: 'Featured product', component: FeaturedProductSection },
  page_links: { name: 'Page links', component: PageLinksSection },
  countdown: { name: 'Countdown', component: CountdownSection },
  before_after: { name: 'Before and after', component: BeforeAfterSection },
  lookbook: { name: 'Shoppable lookbook', component: LookbookSection },
  inventory_urgency: { name: 'Low stock alert', component: InventoryUrgencySection },
  promotion_banner: { name: 'Promotion banner', component: PromotionBannerSection },
  collections: { name: 'Collections', component: CollectionsSection },
  image_with_text: { name: 'Image with text', component: ImageWithTextSection },
  video: { name: 'Video', component: VideoSection },
  contact: { name: 'Contact and location', component: ContactSection },
  contact_form: { name: 'Contact form', component: ContactFormSection },
  feature_columns: { name: 'Feature columns', component: FeatureColumnsSection },
  static_features: { name: 'Store features', component: StaticFeaturesSection },
  bundles: { name: 'Bundles', component: BundlesSection },
  recently_viewed: { name: 'Recently viewed', component: RecentlyViewedSection },
  faq: { name: 'FAQ', component: FaqSection },
  newsletter: { name: 'Newsletter', component: NewsletterSection },
  rich_text: { name: 'Rich text', component: RichTextSection },
  faq_entry: { name: 'Reusable FAQ', component: ReusableFaqSection },
  testimonials_entry: { name: 'Reusable testimonials', component: ReusableTestimonialsSection },
  brand_list: { name: 'Brand list', component: BrandListSection },
  size_guide: { name: 'Size guide', component: SizeGuideSection },
};
